import React, { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../../config/api';
import {
  openContinuationMailto,
  copyContinuationEmailPack,
  formatDaysLeftLabel,
  formatKesDisplay,
  REQUESTS_EMAIL
} from '../../utils/compContinuationEmailPack';
import './CompContinuations.css';

const SCOPES = [
  { id: 'action', label: 'Action needed' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'requested', label: 'Requested' },
  { id: 'all', label: 'All open' }
];

function urgencyClass(key) {
  switch (key) {
    case 'critical':
    case 'urgent':
      return 'comp-cont-urgent-critical';
    case 'soon':
      return 'comp-cont-urgent-soon';
    case 'lapsed_recoverable':
      return 'comp-cont-urgent-lapsed';
    case 'watch':
      return 'comp-cont-urgent-watch';
    default:
      return 'comp-cont-urgent-ok';
  }
}

/**
 * COMP Continuations worklist — underwriting (all covers) or agent (own book).
 * CTA opens the user's own email (mailto → requests@) — not alerts SMTP.
 */
const CompContinuations = ({ authToken, agentProfile, apiBase = '/api/admin/comp-continuations' }) => {
  const [scope, setScope] = useState('action');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({ actionNeeded: 0, upcoming: 0, requested: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [requestsEmail, setRequestsEmail] = useState(REQUESTS_EMAIL);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState(null);

  const showFlash = (message, type = 'info') => {
    setFlash({ message, type });
    setTimeout(() => setFlash(null), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ scope });
      if (searchApplied) params.set('search', searchApplied);
      const res = await fetch(`${getApiUrl(apiBase)}?${params}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Failed to load (${res.status})`);
      }
      setSummary(data.summary || { actionNeeded: 0, upcoming: 0, requested: 0, total: 0 });
      setItems(Array.isArray(data.items) ? data.items : []);
      if (data.requestsEmail) setRequestsEmail(String(data.requestsEmail));
    } catch (e) {
      setError(e.message || 'Failed to load COMP continuations');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, scope, searchApplied]);

  useEffect(() => {
    if (authToken) load();
  }, [authToken, load]);

  const handleRequestContinuation = async (item) => {
    const pack = item.emailPack;
    if (!pack) {
      showFlash('Email pack missing for this policy.', 'error');
      return;
    }
    const opened = openContinuationMailto(pack);
    if (!opened) {
      try {
        await copyContinuationEmailPack(pack);
        showFlash(`Could not open mail app — pack copied. Paste into your email to ${requestsEmail}.`, 'warning');
      } catch (_) {
        showFlash(`Open your email and write to ${requestsEmail} using the details on this card.`, 'warning');
      }
    } else {
      showFlash(`Opened your email to ${requestsEmail}. Attach mechanical report + valuation letter, then send.`, 'success');
    }
  };

  const handleCopyPack = async (item) => {
    try {
      await copyContinuationEmailPack(item.emailPack);
      showFlash('Email pack copied — paste into your mail and send from your address.', 'success');
    } catch (e) {
      showFlash(e.message || 'Copy failed', 'error');
    }
  };

  const handleMarkRequested = async (item) => {
    setBusyId(item.clientId);
    try {
      const res = await fetch(
        getApiUrl(`${apiBase}/${encodeURIComponent(item.clientId)}/mark-requested`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes: 'Confirmed email sent from own mailbox' })
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not update status');
      }
      showFlash('Marked as requested. Underwriting will process after docs arrive at requests@.', 'success');
      await load();
    } catch (e) {
      showFlash(e.message || 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="comp-continuations">
      <div className="comp-cont-header">
        <div>
          <h2 className="comp-cont-title">Extend Comprehensive</h2>
          <p className="comp-cont-subtitle">
            1-month AGENTBIMA COMP policies due for extension of the remaining period.
            Extension is <strong>not done in the portal</strong> — email{' '}
            <a href={`mailto:${requestsEmail}`}>{requestsEmail}</a> from{' '}
            <strong>your own mailbox</strong> with a mechanical report and valuation letter.
          </p>
        </div>
        <button type="button" className="comp-cont-refresh" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {flash && (
        <div className={`comp-cont-flash comp-cont-flash-${flash.type}`}>{flash.message}</div>
      )}

      <div className="comp-cont-summary" role="tablist" aria-label="Extension filters">
        {[
          { id: 'action', value: summary.actionNeeded, label: 'Action needed' },
          { id: 'upcoming', value: summary.upcoming, label: 'Upcoming' },
          { id: 'requested', value: summary.requested, label: 'Requested' },
          { id: 'all', value: summary.total, label: 'Open total' }
        ].map((stat) => (
          <button
            key={stat.id}
            type="button"
            role="tab"
            aria-selected={scope === stat.id}
            className={`comp-cont-stat${scope === stat.id ? ' is-active' : ''}`}
            onClick={() => {
              setScope(stat.id);
              // Keep focus on the worklist after filter change
              requestAnimationFrame(() => {
                document.getElementById('comp-cont-worklist')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              });
            }}
          >
            <span className="comp-cont-stat-value">{stat.value}</span>
            <span className="comp-cont-stat-label">{stat.label}</span>
          </button>
        ))}
      </div>

      <div className="comp-cont-toolbar">
        <div className="comp-cont-scopes">
          {SCOPES.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`comp-cont-scope ${scope === s.id ? 'active' : ''}`}
              onClick={() => setScope(s.id)}
            >
              {s.label}
              {s.id === 'action' && summary.actionNeeded > 0 ? ` (${summary.actionNeeded})` : ''}
            </button>
          ))}
        </div>
        <div className="comp-cont-search">
          <input
            type="search"
            placeholder="Search reg, policy, insured…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearchApplied(search.trim());
            }}
          />
          <button type="button" onClick={() => setSearchApplied(search.trim())}>
            Search
          </button>
        </div>
      </div>

      {error && <div className="comp-cont-error">{error}</div>}

      <div id="comp-cont-worklist">
      {loading && !items.length ? (
        <div className="comp-cont-empty">Loading COMP continuations…</div>
      ) : !items.length ? (
        <div className="comp-cont-empty">
          {scope === 'action'
            ? 'No Comprehensive continuations need action right now.'
            : 'No items in this view.'}
        </div>
      ) : (
        <div className="comp-cont-list">
          {items.map((item) => (
            <article key={item.clientId} className="comp-cont-card">
              <div className="comp-cont-card-top">
                <div>
                  <h3 className="comp-cont-reg">{item.registrationNumber || 'No reg'}</h3>
                  <p className="comp-cont-insured">{item.insuredName || '—'}</p>
                </div>
                <span className={`comp-cont-badge ${urgencyClass(item.urgency?.key)}`}>
                  {item.urgency?.label || '—'} · {formatDaysLeftLabel(item.daysLeft)}
                </span>
              </div>

              <dl className="comp-cont-meta">
                <div>
                  <dt>Policy</dt>
                  <dd>{item.policyNumber || '—'}</dd>
                </div>
                <div>
                  <dt>Certificate</dt>
                  <dd>{item.certificateNumber || '—'}</dd>
                </div>
                <div>
                  <dt>Cover end</dt>
                  <dd>{item.endDate || '—'}</dd>
                </div>
                <div>
                  <dt>Paid (30%)</dt>
                  <dd>{formatKesDisplay(item.paidPremium)}</dd>
                </div>
                <div>
                  <dt>Est. remaining (~70%)</dt>
                  <dd className="comp-cont-remaining">{formatKesDisplay(item.remainingPremium)}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{item.continuationStatus === 'requested' ? 'Email requested' : 'Open'}</dd>
                </div>
              </dl>

              <p className="comp-cont-hint">
                Docs required: <strong>mechanical report</strong> + <strong>valuation letter</strong>.
                Send from your email to <strong>{requestsEmail}</strong>
                {agentProfile?.email ? ` (you: ${agentProfile.email})` : ''}.
              </p>

              <div className="comp-cont-actions">
                <button
                  type="button"
                  className="comp-cont-btn primary"
                  onClick={() => handleRequestContinuation(item)}
                >
                  Request extension
                </button>
                <button type="button" className="comp-cont-btn" onClick={() => handleCopyPack(item)}>
                  Copy email pack
                </button>
                {item.continuationStatus !== 'requested' && (
                  <button
                    type="button"
                    className="comp-cont-btn ghost"
                    disabled={busyId === item.clientId}
                    onClick={() => handleMarkRequested(item)}
                  >
                    {busyId === item.clientId ? 'Saving…' : 'I sent the email'}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default CompContinuations;
